if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };
        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
        this._wasAtEndOnTouchStart = false;
        this._wasAtBeginningOnTouchStart = false;
        this._boundNextButtonHandler = null;
        this._boundPrevButtonHandler = null;
        this._boundViewerWheelHandler = null;
        this._lastAutoTabSwitchAt = 0;
        this._pendingSlideTo = null;
      }

      connectedCallback() {
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      init() {
        if (this.initialized === true) return;
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;

        this.enableMediaTabs = this.dataset.enableMediaTabs === 'true';
        if (this.enableMediaTabs) {
          this._setupMediaTabs();
        }

        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        if (this.enableMediaTabs) {
          this._bindTabEvents();
        }

        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal',
            },
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap,
            },
          },
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap,
              },
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5,
              },
              1536: {
                slidesPerView: 7,
              },
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();
          this.handleTabAutoAdvance();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

            let source = {
              id: mediaIndex,
              mediaType: mediaType,
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video':
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

                source = {
                  ...source,
                  html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`,
                };
                break;
              default: // Image.
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight,
                };
                break;
            }

            dataSource.push(source);
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

       this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          const slider = this.sliderInstance?.slider;

          if (slider) {
            const { slides, activeIndex } = slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        // Store the current index before closing
        let lastLightboxIndex = null;
        this.lightbox.on('close', () => {
          if (this.lightbox?.pswp?.currIndex) {
            lastLightboxIndex = this.lightbox.pswp.currIndex;
          }
        });

        // Update slider after lightbox is destroyed
        this.lightbox.on('destroy', () => {
          const slider = this.sliderInstance?.slider;

          if (slider && lastLightboxIndex !== null) {
            const targetIndex = lastLightboxIndex;
            // currIndex from lightbox is the index in dataSource array
            // which should match the slider index since both are created from the same media items in the same order
            if (targetIndex >= 0 && targetIndex < slider.slides.length && slider.activeIndex !== targetIndex) {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                slider.slideTo(targetIndex, 0);
              });
            }
            lastLightboxIndex = null;
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              },
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              },
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
           if (media.dataset?.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }

          this._syncTabAwareNavButtons(swiper);
        });
      }

      handleTabAutoAdvance() {
        if (!this.enableMediaTabs || !this.sliderInstance?.slider) return;

        const swiper = this.sliderInstance.slider;

        swiper.on('afterInit', () => {
          this._syncTabAwareNavButtons(swiper);
        });

        swiper.on('fromEdge', () => {
          this._syncTabAwareNavButtons(swiper);
        });

        swiper.on('toEdge', () => {
          this._syncTabAwareNavButtons(swiper);
        });

        // If coming back from a forward tab switch, jump to last slide once swiper inits
        if (this._pendingSlideTo === 'end') {
          this._pendingSlideTo = null;
          swiper.once('init', () => {
            swiper.slideTo(swiper.slides.length - 1, 0, false);
            this._syncTabAwareNavButtons(swiper);
          });
        }

        swiper.on('touchStart', () => {
          this._wasAtEndOnTouchStart = swiper.isEnd;
          this._wasAtBeginningOnTouchStart = swiper.isBeginning;
        });

        swiper.on('touchEnd', () => {
          // Switch tab when user swipes forward again while already at the end.
          const isForwardSwipe = swiper.swipeDirection === 'next' || swiper?.touches?.diff < 0;
          if (swiper.isEnd && this._wasAtEndOnTouchStart && isForwardSwipe) {
            this._tryAutoAdvanceToNextTab();
          }
          // Switch tab when user swipes backward again while already at the beginning.
          const isBackwardSwipe = swiper.swipeDirection === 'prev' || swiper?.touches?.diff > 0;
          if (swiper.isBeginning && this._wasAtBeginningOnTouchStart && isBackwardSwipe) {
            this._tryAutoAdvanceToPrevTab();
          }
        });

        const nextButton = this.querySelector('.swiper-button-next');
        if (nextButton) {
          if (this._boundNextButtonHandler) {
            nextButton.removeEventListener('click', this._boundNextButtonHandler);
          }
          this._boundNextButtonHandler = () => {
            if (swiper.isEnd) {
              this._tryAutoAdvanceToNextTab();
            }
          };
          nextButton.addEventListener('click', this._boundNextButtonHandler);
        }

        const prevButton = this.querySelector('.swiper-button-prev');
        if (prevButton) {
          if (this._boundPrevButtonHandler) {
            prevButton.removeEventListener('click', this._boundPrevButtonHandler);
          }
          this._boundPrevButtonHandler = () => {
            if (swiper.isBeginning) {
              this._tryAutoAdvanceToPrevTab();
            }
          };
          prevButton.addEventListener('click', this._boundPrevButtonHandler);
        }

        this._syncTabAwareNavButtons(swiper);

        if (this.elements?.viewer) {
          if (this._boundViewerWheelHandler) {
            this.elements.viewer.removeEventListener('wheel', this._boundViewerWheelHandler);
          }
          this._boundViewerWheelHandler = (event) => {
            // A single-slide tab (e.g. a Video tab with one clip) is always both
            // isBeginning and isEnd at once. Without this guard, every normal
            // page-scroll tick over the gallery would be treated as "at the edge"
            // and hijacked into a tab switch. Let single-slide tabs scroll normally.
            if (swiper.slides.length <= 1) return;

            if (event.deltaY > 0) {
              if (!swiper.isEnd) return;
              event.preventDefault();
              this._tryAutoAdvanceToNextTab();
            } else if (event.deltaY < 0) {
              if (!swiper.isBeginning) return;
              event.preventDefault();
              this._tryAutoAdvanceToPrevTab();
            }
          };
          this.elements.viewer.addEventListener('wheel', this._boundViewerWheelHandler, { passive: false });
        }
      }

      _tryAutoAdvanceToNextTab() {
        const swiper = this.sliderInstance?.slider;
        // Same single-slide guard as the wheel handler — also covers touch swipes
        // and the prev/next nav buttons, which funnel through this method.
        if (swiper && swiper.slides.length <= 1) return false;

        const now = Date.now();
        if (now - this._lastAutoTabSwitchAt < 300) return;

        const switched = this._switchToNextTab();
        if (switched) {
          this._lastAutoTabSwitchAt = now;
        }
      }

      _tryAutoAdvanceToPrevTab() {
        const swiper = this.sliderInstance?.slider;
        if (swiper && swiper.slides.length <= 1) return false;

        const now = Date.now();
        if (now - this._lastAutoTabSwitchAt < 300) return;

        const switched = this._switchToPrevTab();
        if (switched) {
          this._lastAutoTabSwitchAt = now;
        }
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        // When media tabs are active, only switch media in 'product' tab
        if (this.enableMediaTabs && this._activeTab !== 'product') {
          this._pendingVariant = variant;
          return;
        }

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image',
          };
        }

        if (!featuredMedia) return;

        if (this.enableMediaTabs) {
          // Find the slide index within the currently visible (in-DOM) slides
          const visibleSlides = this.elements.mediaList.querySelectorAll('.product__media-item');
          let targetIndex = -1;
          visibleSlides.forEach((slide, idx) => {
            if (slide.dataset.mediaId == featuredMedia.id) targetIndex = idx;
          });
          if (targetIndex >= 0) {
            if (this.sliderInstance && this.sliderInstance.slider) {
              this.sliderInstance.slider.slideTo(targetIndex, 0, false);
            } else {
              // Stacked layout: scroll into view
              visibleSlides[targetIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          }
          return;
        }

        if (this.sliderInstance.slider) {
          const slideIndex = featuredMedia.position || 0;
          this.sliderInstance.slider.slideTo(slideIndex - 1, 0, false);
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image',
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            // scrollIntoView with block:'nearest' scrolls only as much as needed to
            // bring the whole image into view, instead of forcing its top edge to
            // the top of the window (which could push its bottom off-screen).
            selectedMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }
      }

      /* ═══════════════════════════════════════════════════════
       *  Media Category Tabs
       * ═══════════════════════════════════════════════════════ */
      _extractCategoryFromElement(el) {
        // Trust Shopify's actual media type first — video poster filenames/alt text
        // are often auto-generated and won't match the [video] tag or "video" filename prefix.
        const mediaType = el.dataset.mediaType || el.querySelector('[data-media-type]')?.dataset.mediaType;
        if (mediaType === 'video' || mediaType === 'external_video') return 'video';

        // Check alt text or image filename for tags [product], [scene], [video]
        const altText = el.getAttribute('alt') || el.querySelector('img')?.getAttribute('alt') || '';
        const mediaSrc = el.dataset.src || el.getAttribute('src') || el.querySelector('img')?.getAttribute('src') || '';
        let fileName = '';

        try {
          fileName = decodeURIComponent(mediaSrc.split('?')[0].split('/').pop() || '');
        } catch (error) {
          fileName = mediaSrc.split('?')[0].split('/').pop() || '';
        }

        const combinedText = `${altText} ${fileName}`.toLowerCase();
        const fileNameLower = fileName.toLowerCase();

        if (combinedText.includes('[scene]') || fileNameLower.startsWith('scene')) return 'scene';
        if (combinedText.includes('[video]') || fileNameLower.startsWith('video')) return 'video';
        if (combinedText.includes('[product]')) return 'product';

        // Default to product
        return 'product';
      }

      _setupMediaTabs() {
        this._activeTab = 'product';
        this._pendingVariant = null;
        this._tabButtons = this.querySelectorAll('[data-media-tab]');

        // Store all slides and thumbs with category info before slider init
        this._allSlideData = [...this.elements.mediaList.querySelectorAll('.product__media-item')].map(el => ({
          element: el,
          category: this._extractCategoryFromElement(el)
        }));

        const thumbsList = this.querySelector('.thumbnail-list');
        this._thumbsList = thumbsList;
        this._allThumbData = thumbsList
          ? [...thumbsList.querySelectorAll('.product__thumbs-item')].map(el => ({
              element: el,
              category: this._extractCategoryFromElement(el)
            }))
          : [];

        // Apply initial filter BEFORE first slider init — only product slides in DOM
        this._applyTabFilter('product');
      }

      _bindTabEvents() {
        this._tabButtons.forEach((btn) => {
          const tab = btn.dataset.mediaTab;

          if (tab === 'video') {
            const warmupHandler = () => this._warmupVideoTabMedia('video', 1);
            btn.addEventListener('mouseenter', warmupHandler, { passive: true });
            btn.addEventListener('focus', warmupHandler, { passive: true });
            btn.addEventListener('touchstart', warmupHandler, { passive: true });
          }

          btn.addEventListener('click', () => {
            const tab = btn.dataset.mediaTab;
            if (tab === this._activeTab) return;
            this._switchTab(tab);
          });
          btn.addEventListener('mouseenter', () => {
            // On hover: visually show this as active, hide current active style
            this._tabButtons.forEach(b => b.classList.remove('media-tabs__btn--hover'));
            if (btn.dataset.mediaTab !== this._activeTab) {
              btn.classList.add('media-tabs__btn--hover');
              this._tabButtons.forEach(b => {
                if (b.dataset.mediaTab === this._activeTab) {
                  b.classList.add('media-tabs__btn--dimmed');
                }
              });
            }
          });
          btn.addEventListener('mouseleave', () => {
            // On leave: restore active state
            btn.classList.remove('media-tabs__btn--hover');
            this._tabButtons.forEach(b => b.classList.remove('media-tabs__btn--dimmed'));
          });
        });

        // Warm up the first video player once gallery logic is ready.
        this._warmupVideoTabMedia('video', 1);
      }

      _warmupVideoTabMedia(tab = 'video', maxCount = 1) {
        if (!this._allSlideData || !this._allSlideData.length) return;

        let warmed = 0;
        for (const { element, category } of this._allSlideData) {
          if (category !== tab) continue;

          const videoElement = element.querySelector('video-element');
          if (!videoElement || videoElement.dataset.prewarmed === 'true') continue;

          videoElement.dataset.prewarmed = 'true';
          // Accessing `player` triggers internal initialization once per element.
          void videoElement.player;

          warmed += 1;
          if (warmed >= maxCount) break;
        }
      }

      _categoryMatchesTab(category, tab) {
        // Videos are shown in both the Products tab and their own Video tab,
        // so a product shot's video still surfaces alongside the other product media.
        if (tab === 'product') {
          return category === 'product' || category === 'video';
        }
        return category === tab;
      }

      _applyTabFilter(tab) {
        // Put matching slides into the swiper-wrapper, remove others
        const wrapper = this.elements.mediaList;
        this._allSlideData.forEach(({ element, category }) => {
          if (this._categoryMatchesTab(category, tab)) {
            if (!wrapper.contains(element)) wrapper.appendChild(element);
          } else {
            if (wrapper.contains(element)) wrapper.removeChild(element);
          }
        });

        // Same for thumbnails
        if (this._thumbsList) {
          this._allThumbData.forEach(({ element, category }) => {
            if (this._categoryMatchesTab(category, tab)) {
              if (!this._thumbsList.contains(element)) this._thumbsList.appendChild(element);
            } else {
              if (this._thumbsList.contains(element)) this._thumbsList.removeChild(element);
            }
          });
        }
      }

      _switchTab(tab) {
        this._activeTab = tab;

        // Update button states
        this._tabButtons.forEach((btn) => {
          const isActive = btn.dataset.mediaTab === tab;
          btn.classList.toggle('media-tabs__btn--active', isActive);
          btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        // Move slides in/out of DOM
        this._applyTabFilter(tab);

        this._rebuildSlider();

        // Apply pending variant when switching back to product tab
        if (tab === 'product' && this._pendingVariant) {
          requestAnimationFrame(() => {
            this.setActiveMedia(this._pendingVariant);
            this._pendingVariant = null;
          });
        }
      }

      _switchToNextTab() {
        if (!this._tabButtons || this._tabButtons.length < 2) return;

        const tabOrder = [...this._tabButtons].map((btn) => btn.dataset.mediaTab).filter(Boolean);
        const currentIndex = tabOrder.indexOf(this._activeTab);
        if (currentIndex === -1 || currentIndex >= tabOrder.length - 1) return false;

        this._switchTab(tabOrder[currentIndex + 1]);
        return true;
      }

      _switchToPrevTab() {
        if (!this._tabButtons || this._tabButtons.length < 2) return false;

        const tabOrder = [...this._tabButtons].map((btn) => btn.dataset.mediaTab).filter(Boolean);
        const currentIndex = tabOrder.indexOf(this._activeTab);
        if (currentIndex <= 0) return false;

        this._pendingSlideTo = 'end';
        this._switchTab(tabOrder[currentIndex - 1]);
        return true;
      }

      _getTabOrder() {
        if (!this._tabButtons || !this._tabButtons.length) return [];
        return [...this._tabButtons].map((btn) => btn.dataset.mediaTab).filter(Boolean);
      }

      _hasNextTab() {
        const tabOrder = this._getTabOrder();
        const currentIndex = tabOrder.indexOf(this._activeTab);
        return currentIndex > -1 && currentIndex < tabOrder.length - 1;
      }

      _hasPrevTab() {
        const tabOrder = this._getTabOrder();
        const currentIndex = tabOrder.indexOf(this._activeTab);
        return currentIndex > 0;
      }

      _setButtonDisabledState(button, isDisabled) {
        if (!button) return;

        button.classList.toggle('swiper-button-disabled', isDisabled);
        button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

        // Swiper can set `disabled` on button elements, blocking click events entirely.
        if (isDisabled) {
          button.setAttribute('disabled', 'disabled');
        } else {
          button.removeAttribute('disabled');
        }
      }

      _syncTabAwareNavButtons(swiper = this.sliderInstance?.slider) {
        if (!this.enableMediaTabs || !swiper) return;

        const nextButton = this.querySelector('.swiper-button-next');
        const prevButton = this.querySelector('.swiper-button-prev');

        const shouldEnableNextForTabSwitch = swiper.isEnd && this._hasNextTab();
        const shouldEnablePrevForTabSwitch = swiper.isBeginning && this._hasPrevTab();

        if (nextButton && shouldEnableNextForTabSwitch) {
          this._setButtonDisabledState(nextButton, false);
        }

        if (prevButton && shouldEnablePrevForTabSwitch) {
          this._setButtonDisabledState(prevButton, false);
        }
      }

      _rebuildSlider() {
        // Destroy existing slider
        if (typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
        if (typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider) {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
        }

        // Re-init slider with only visible slides
        requestAnimationFrame(() => {
          this.updateMediaLayout();
          // Re-init zoom for current visible slides
          if (this.enableImageZoom && this.lightbox) {
            this.lightbox.destroy();
            this.lightbox = null;
            this.initImageZoom();
          }
        });
      }
    }
  );
}